import React from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../styles/LoanAcceptance.css';

function SignedContract({ loan, onClose }) {
  
  function handlePrint() {
    window.print();
  }

  function handleDownloadPDF() {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Family Loan Agreement', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Date
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Date: ${loan.acceptedAt ? new Date(loan.acceptedAt).toLocaleDateString() : new Date().toLocaleDateString()}`, 20, yPos);
    yPos += 10;

    // Parties
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Parties:', 20, yPos);
    yPos += 7;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Lender: ${loan.loanerName}`, 20, yPos);
    yPos += 6;
    doc.text(`Borrower: ${loan.loaneeName}`, 20, yPos);
    yPos += 12;

    // Loan Details Summary
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Loan Details', 20, yPos);
    yPos += 7;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    const details = [
      `Loan Amount: $${loan.amount.toFixed(2)}`,
      `Interest Rate: ${loan.interestRate}% APR`,
      `Term: ${loan.termLength} ${loan.termType}`,
      `Monthly Payment: $${loan.monthlyPayment.toFixed(2)}`,
      `Total Interest: $${loan.totalInterest.toFixed(2)}`,
      `Total Amount to Repay: $${(loan.amount + loan.totalInterest).toFixed(2)}`,
      `First Payment Date: ${loan.startDate ? new Date(loan.startDate).toLocaleDateString() : 'TBD'}`
    ];

    details.forEach(detail => {
      doc.text(detail, 20, yPos);
      yPos += 6;
    });
    yPos += 8;

    // Agreement Sections
    const sections = [
      {
        title: '1) Loan Amount & Purpose',
        text: `Lender agrees to loan Borrower $${loan.amount.toFixed(2)} ("Principal") for ${loan.purpose}.`
      },
      {
        title: '2) Interest Rate',
        text: `Fixed annual interest rate of ${loan.interestRate}% APR, calculated on the outstanding principal balance.`
      },
      {
        title: '3) Repayment Terms',
        text: `Borrower will repay the loan in ${loan.termLength} equal monthly payments. First payment due: ${loan.startDate ? new Date(loan.startDate).toLocaleDateString() : 'TBD'}. Borrower may prepay all or part at any time without penalty.`
      },
      {
        title: '4) Method of Payment',
        text: 'By cash, check, bank transfer, or another mutually agreed method to the Lender.'
      },
      {
        title: '5) Late Payments',
        text: 'A payment not received within 10 calendar days after its due date may be assessed a $15.00 late fee.'
      },
      {
        title: '6) Default',
        text: 'If any amount remains unpaid 30 days after its due date, the remaining unpaid principal and accrued interest may be declared immediately due and payable.'
      },
      {
        title: '7) No Assignment',
        text: 'Borrower may not assign this Agreement without Lender\'s written consent.'
      },
      {
        title: '8) Governing Law',
        text: 'This Agreement is governed by the laws of the State of Idaho.'
      },
      {
        title: '9) Entire Agreement',
        text: 'This document contains the entire agreement. Any changes must be in writing and signed by both parties.'
      }
    ];

    sections.forEach(section => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFont(undefined, 'bold');
      doc.text(section.title, 20, yPos);
      yPos += 6;
      doc.setFont(undefined, 'normal');
      const lines = doc.splitTextToSize(section.text, pageWidth - 40);
      doc.text(lines, 20, yPos);
      yPos += (lines.length * 5) + 5;
    });

    // Payment Schedule
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`Payment Schedule (${loan.interestRate}% APR, ${loan.termLength} payments)`, 20, yPos);
    yPos += 7;

    const tableData = loan.amortizationSchedule?.map(row => [
      row.period,
      row.paymentDate ? new Date(row.paymentDate).toLocaleDateString() : 'TBD',
      `$${row.payment.toFixed(2)}`,
      `$${row.interest.toFixed(2)}`,
      `$${row.principal.toFixed(2)}`,
      `$${row.balance.toFixed(2)}`
    ]) || [];

    autoTable(doc, {
      startY: yPos,
      head: [['Pmt #', 'Due Date', 'Payment', 'Interest', 'Principal', 'Balance']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [5, 114, 206], textColor: 255 },
      styles: { fontSize: 8 },
      margin: { left: 20, right: 20 }
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Totals
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(`Total of Payments: $${(loan.amount + loan.totalInterest).toFixed(2)}`, 20, yPos);
    yPos += 6;
    doc.text(`Total Interest: $${loan.totalInterest.toFixed(2)}`, 20, yPos);
    yPos += 6;
    doc.text(`Principal: $${loan.amount.toFixed(2)}`, 20, yPos);
    yPos += 12;

    // Signature
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Electronic Signature', 20, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Signed by: ${loan.loaneeSignature}`, 20, yPos);
    yPos += 6;
    doc.text(`Date & Time: ${loan.loaneeSignedAt ? new Date(loan.loaneeSignedAt).toLocaleString() : 'N/A'}`, 20, yPos);
    yPos += 6;
    doc.text(`Agreement Accepted: ${loan.agreementAccepted ? 'Yes' : 'No'}`, 20, yPos);

    // Save PDF
    doc.save(`Loan_Agreement_${loan.loaneeName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  }

  return (
    <div className="loan-acceptance-container">
      <div className="contract-actions no-print">
        <button className="btn btn-primary" onClick={handleDownloadPDF}>
          📥 Download PDF
        </button>
        <button className="btn btn-secondary" onClick={handlePrint}>
          🖨️ Print Contract
        </button>
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>

      <h2>Signed Loan Agreement</h2>
      
      <div className="loan-summary-section">
        <h3>Loan Details</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">Loan Amount:</span>
            <span className="value">${loan.amount.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Interest Rate:</span>
            <span className="value">{loan.interestRate}%</span>
          </div>
          <div className="summary-item">
            <span className="label">Term:</span>
            <span className="value">{loan.termLength} {loan.termType}</span>
          </div>
          <div className="summary-item">
            <span className="label">Monthly Payment:</span>
            <span className="value">${loan.monthlyPayment.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Total Interest:</span>
            <span className="value">${loan.totalInterest.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Total Amount to Repay:</span>
            <span className="value highlight">${(loan.amount + loan.totalInterest).toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="label">First Payment Date:</span>
            <span className="value">{loan.startDate ? new Date(loan.startDate).toLocaleDateString() : 'N/A'}</span>
          </div>
          <div className="summary-item">
            <span className="label">Loaner:</span>
            <span className="value">{loan.loanerName}</span>
          </div>
        </div>
      </div>

      <div className="terms-section">
        <h3>Family Loan Agreement</h3>
        <div className="terms-content">
          <p><strong>Date:</strong> {loan.acceptedAt ? new Date(loan.acceptedAt).toLocaleDateString() : new Date().toLocaleDateString()}</p>
          
          <h4>Parties:</h4>
          <p><strong>Lender:</strong> {loan.loanerName}</p>
          <p><strong>Borrower:</strong> {loan.loaneeName}</p>

          <h4>1) Loan Amount & Purpose</h4>
          <p>Lender agrees to loan Borrower <strong>${loan.amount.toFixed(2)}</strong> ("Principal") for {loan.purpose}.</p>

          <h4>2) Interest Rate</h4>
          <p>Fixed annual interest rate of <strong>{loan.interestRate}% APR</strong>, calculated on the outstanding principal balance.</p>

          <h4>3) Repayment Terms</h4>
          <p>Borrower will repay the loan in <strong>{loan.termLength} equal monthly payments</strong> as shown in the Payment Schedule below. Payments include principal and interest.</p>
          <p><strong>First payment due:</strong> {loan.startDate ? new Date(loan.startDate).toLocaleDateString() : 'TBD'}</p>
          <p>Borrower may prepay all or part at any time without penalty. Prepayments apply first to accrued interest, then to principal.</p>

          <h4>4) Method of Payment</h4>
          <p>By cash, check, bank transfer, or another mutually agreed method to the Lender.</p>

          <h4>5) Late Payments</h4>
          <p>A payment not received within 10 calendar days after its due date may be assessed a $15.00 late fee. Interest continues to accrue on unpaid principal.</p>

          <h4>6) Default</h4>
          <p>If any amount remains unpaid 30 days after its due date, the remaining unpaid principal and accrued interest may be declared immediately due and payable at the Lender's option.</p>

          <h4>7) No Assignment</h4>
          <p>Borrower may not assign this Agreement without Lender's written consent.</p>

          <h4>8) Governing Law</h4>
          <p>This Agreement is governed by the laws of the State of Idaho.</p>

          <h4>9) Entire Agreement</h4>
          <p>This document contains the entire agreement. Any changes must be in writing and signed by both parties.</p>

          {loan.notes && (
            <div className="additional-terms">
              <h4>Additional Terms:</h4>
              <p>{loan.notes}</p>
            </div>
          )}

          <h4>Payment Schedule ({loan.interestRate}% APR, {loan.termLength} payments)</h4>
          <div className="payment-schedule-summary">
            <table className="schedule-table">
              <thead>
                <tr>
                  <th>Pmt #</th>
                  <th>Due Date</th>
                  <th>Payment</th>
                  <th>Interest</th>
                  <th>Principal</th>
                  <th>Balance After</th>
                </tr>
              </thead>
              <tbody>
                {loan.amortizationSchedule?.map((row) => (
                  <tr key={row.period}>
                    <td>{row.period}</td>
                    <td>{row.paymentDate ? new Date(row.paymentDate).toLocaleDateString() : 'TBD'}</td>
                    <td>${row.payment.toFixed(2)}</td>
                    <td>${row.interest.toFixed(2)}</td>
                    <td>${row.principal.toFixed(2)}</td>
                    <td>${row.balance.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="schedule-totals">
              <p><strong>Total of Payments:</strong> ${(loan.amount + loan.totalInterest).toFixed(2)}</p>
              <p><strong>Total Interest:</strong> ${loan.totalInterest.toFixed(2)}</p>
              <p><strong>Principal:</strong> ${loan.amount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="signature-section">
        <h3>Electronic Signatures</h3>
        
        <div className="signature-record">
          <h4>Borrower Signature</h4>
          <p><strong>Signed by:</strong> {loan.loaneeSignature}</p>
          <p><strong>Date & Time:</strong> {loan.loaneeSignedAt ? new Date(loan.loaneeSignedAt).toLocaleString() : 'N/A'}</p>
          <p><strong>Agreement Accepted:</strong> {loan.agreementAccepted ? 'Yes' : 'No'}</p>
        </div>

        <div className="signature-disclaimer">
          <p><small>
            This is a legally binding electronic signature. Both parties have agreed to the terms and conditions outlined in this loan agreement.
          </small></p>
        </div>
      </div>
    </div>
  );
}

export default SignedContract;
